export type EqualsFunction<T> = (a: T | undefined, b: T | undefined) => boolean;
export type EqualsFunctionOption = EqualsFunction<any> | "ref" | "shallow" | "level2" | "level3" | "level4" | "level5" | "deep"

export class CompareUtil {
  static refEqual<T>(a: T, b: T): boolean {
    return a === b;
  }

  static shallowEqualArrays(a: any[], b: any[]): boolean {
    if (a === b) return true;
    if (!a || !b) return false;

    const len = a.length;

    if (b.length !== len) {
      return false;
    }

    for (let i = 0; i < len; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }

    return true;
  }

  static shallowEqualObjects(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return false;

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    const len = aKeys.length;

    if (bKeys.length !== len) {
      return false;
    }

    for (let i = 0; i < len; i++) {
      const key = aKeys[i];

      const equal = a[key] === b[key];
      if (!equal || !Object.prototype.hasOwnProperty.call(b, key)) {
        return false;
      }
    }

    return true;
  }

  static shallowEqualObjectsWithIgnore(a: any, b: any, options?: { ignoreList?: string[] }): boolean {
    if (a === b) return true;
    if (!a || !b) return false;

    const aKeys = Object.keys(a).filter(k => !options?.ignoreList?.includes(k));
    const bKeys = Object.keys(b).filter(k => !options?.ignoreList?.includes(k));
    const len = aKeys.length;

    if (bKeys.length !== len) {
      return false;
    }

    for (let i = 0; i < len; i++) {
      const key = aKeys[i];

      const equal = a[key] === b[key];
      if (!equal || !Object.prototype.hasOwnProperty.call(b, key)) {
        return false;
      }
    }

    return true;
  }

  static shallowEqual(a: any, b: any): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }

    if (Array.isArray(a)) {
      if (Array.isArray(b))
        return CompareUtil.shallowEqualArrays(a, b);
      return false;
    } else if (Array.isArray(b)) {
      return false;
    } else if (typeof a === "object" && typeof b === "object") {
      return CompareUtil.shallowEqualObjects(a, b);
    }
    return false;
  }

  static equalLevel(a: any, b: any, depth: number = Number.MAX_VALUE): boolean {
    if (depth === 0) {
      return a === b;
    }

    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }

    if (Array.isArray(a)) {
      if (Array.isArray(b)) {
        if (a.length !== b.length) {
          return false;
        }
        for (let i = 0; i < a.length; i++) {
          if (!CompareUtil.equalLevel(a[i], b[i], depth - 1)) {
            return false;
          }
        }
        return true;
      }
      return false;
    } else if (Array.isArray(b))
      return false;
    else if (typeof a === "object" && typeof b === "object") {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      const len = aKeys.length;

      if (bKeys.length !== len) {
        return false;
      }

      for (let i = 0; i < len; i++) {
        const key = aKeys[i];

        if (!CompareUtil.equalLevel(a[key], b[key], depth - 1) || !Object.prototype.hasOwnProperty.call(b, key)) {
          return false;
        }
      }
      return true
    }

    return false;
  }

  static equalLevel2(a: any, b: any): boolean {
    return CompareUtil.equalLevel(a, b, 2);
  }

  static equalLevel3(a: any, b: any): boolean {
    return CompareUtil.equalLevel(a, b, 3);
  }

  static equalLevel4(a: any, b: any): boolean {
    return CompareUtil.equalLevel(a, b, 4);
  }

  static equalLevel5(a: any, b: any): boolean {
    return CompareUtil.equalLevel(a, b, 5);
  }

  static equalDeep(a: any, b: any): boolean {
    return CompareUtil.equalLevel(a, b);
  }

  static getEqualsFunction(equalityFunction: EqualsFunctionOption | undefined): EqualsFunction<any> {
    switch (equalityFunction) {
      case undefined:
      case "ref":
        return CompareUtil.refEqual;
      case "shallow":
        return CompareUtil.shallowEqual;
      case "level2":
        return CompareUtil.equalLevel2;
      case "level3":
        return CompareUtil.equalLevel3;
      case "level4":
        return CompareUtil.equalLevel4;
      case "level5":
        return CompareUtil.equalLevel5;
      case "deep":
        return CompareUtil.equalDeep;
    }
    return equalityFunction;
  }

  static arrayStartsWith<T>(aa: readonly T[], bb: readonly T[]): boolean {
    if (aa.length >= bb.length) {
      for (let i = 0; i < bb.length; i++) {
        if (aa[i] !== bb[i])
          return false;
      }
      return true;
    }
    return false;
  }

  static arrayEquals<T>(aa: readonly T[], bb: readonly T[]): boolean {
    if (aa.length !== bb.length)
      return false;
    for (let i = 0; i < aa.length; i++) {
      if (aa[i] !== bb[i])
        return false;
    }
    return true;
  }
}
